import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import styles from "./COIResultsApprovalViewer.module.css";
import {
  fetchConflictMatches,
  saveConflictDecisions,
  SaveConflictDecisionPayload,
} from "../../../../firm_setup/ConflictCheckResults/ConflictCheckResults.service";
import { AFFILIATE_TYPE_CODES, COIConflictMatch, COI_DECISIONS } from "../../../../firm_setup/ConflictCheckResults/ConflictCheckResults.types";
import { affiliateDisplayName } from "../../../../firm_setup/ConflictCheckResults/ConflictCheckResults.utils";
import { COIResultsApprovalViewerProps } from "./COIResultsApprovalViewer.types";
import { affiliateIsSecClientLabel, decisionBadgeClass, registryClientDisplayName } from "./COIResultsApprovalViewer.utils";

const COIResultsApprovalViewer: React.FC<COIResultsApprovalViewerProps> = ({ sharedData, onActionsChange }) => {
  const { transporter } = sharedData;
  const preloaded = Boolean(sharedData.coiReviewMode);

  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<COIConflictMatch[]>([]);
  const [canDecide, setCanDecide] = useState(false);
  const [decisions, setDecisions] = useState<Record<number, { Decision?: "Proceed" | "Stop"; Comment: string }>>({});
  const [saving, setSaving] = useState<"draft" | "submit" | null>(null);

  const applyMatches = (fetched: COIConflictMatch[], decideAllowed: boolean) => {
    setMatches(fetched);
    setCanDecide(decideAllowed);
    setDecisions((current) => {
      const next: typeof current = {};
      fetched.forEach((m) => {
        next[m.COIConflictMatchId] = current[m.COIConflictMatchId] ?? {
          Decision: m.Decision ?? undefined,
          Comment: m.Comment ?? "",
        };
      });
      return next;
    });
  };

  const load = async () => {
    if (!sharedData.clientMasterId) return;
    setLoading(true);
    try {
      const { matches: fetched, canDecide: decideAllowed } = await fetchConflictMatches(
        sharedData.clientMasterId,
        sharedData.processedTemplateId
      );
      applyMatches(fetched, decideAllowed);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (preloaded) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded, sharedData.clientMasterId, sharedData.processedTemplateId]);

  useEffect(() => {
    if (!preloaded) return;
    setLoading(Boolean(sharedData.coiMatchesLoading));
    if (sharedData.coiMatches) {
      applyMatches(sharedData.coiMatches, Boolean(sharedData.coiCanDecide));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded, sharedData.coiMatches, sharedData.coiCanDecide, sharedData.coiMatchesLoading]);

  const setDecision = (matchId: number, decision: "Proceed" | "Stop") => {
    setDecisions((current) => ({
      ...current,
      [matchId]: { ...current[matchId], Decision: decision },
    }));
  };

  const setComment = (matchId: number, comment: string) => {
    setDecisions((current) => ({
      ...current,
      [matchId]: { ...current[matchId], Comment: comment },
    }));
  };

  const buildPayload = (): SaveConflictDecisionPayload[] =>
    matches.map((m) => ({
      COIConflictMatchId: m.COIConflictMatchId,
      Decision: decisions[m.COIConflictMatchId]?.Decision as "Proceed" | "Stop",
      Comment: decisions[m.COIConflictMatchId]?.Comment,
    }));

  const allDecided = matches.length > 0 && matches.every((m) => decisions[m.COIConflictMatchId]?.Decision);

  const handleSubmit = async (asDraft: boolean) => {
    if (!sharedData.clientMasterId) return;
    setSaving(asDraft ? "draft" : "submit");
    try {
      const payload = asDraft ? buildPayload().filter((d) => d.Decision|| d.Comment?.trim()) : buildPayload();
      const success = await saveConflictDecisions(
        sharedData.clientMasterId,
        payload,
        asDraft,
        sharedData.processedTemplateId
      );
      if (success) {
        if (transporter) {
          transporter(sharedData.processedTemplateId ? "approval_details" : "client_approval");
        } else {
          await load();
        }
      }
    } finally {
      setSaving(null);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const submit = useCallback((asDraft: boolean) => handleSubmitRef.current(asDraft), []);

  useEffect(() => {
    if (!onActionsChange) return;
    onActionsChange(canDecide ? { allDecided, saving, submit } : null);
    return () => onActionsChange(null);
  }, [onActionsChange, canDecide, allDecided, saving, submit]);

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.approvalLoader}>
          <div className="flex-row-center-center" style={{ marginLeft: 5 }}>
            <div style={{ width: "fit-content", padding: 0 }}>
              <ThreeCircles
                height="35"
                width="35"
                color="#4fa94d"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
            </div>
            <h6 style={{ width: "fit-content", marginLeft: 10 }}>Loading conflict check results</h6>
          </div>
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Icon.ShieldCheck className={styles.noConflictIcon} />
        <p>No conflict found.</p>
      </div>
    );
  }

  if (!canDecide) {
    return (
      <div className={styles.matchesGrid}>
        {matches.map((m) => (
          <div key={m.COIConflictMatchId} className={styles.matchCard}>
            <div className={styles.matchTopRow}>
              <div className={styles.matchTitleRow}>
                <div className={styles.matchName}>{affiliateDisplayName(m)}</div>
                <span className={styles.matchScoreBadge}>{m.MatchScore}% match</span>
              </div>
              <span className={`${styles.decisionBadge} ${decisionBadgeClass(m.Decision)}`}>
                {m.Decision || "Pending"}
              </span>
            </div>

            <div className={styles.matchMeta}>{m.AffiliateTypeCode === AFFILIATE_TYPE_CODES.ENTITY ? "Entity" : "Individual"}</div>

            {m.RelationshipToSECClient && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Relationship:</span> {m.RelationshipToSECClient}
              </div>
            )}
            {registryClientDisplayName(m) && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Client:</span> {registryClientDisplayName(m)}
              </div>
            )}
            {m.PrimaryPartner && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Partner:</span> {m.PrimaryPartner}
              </div>
            )}
            {affiliateIsSecClientLabel(m) && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Affiliate Is Client:</span> {affiliateIsSecClientLabel(m)}
              </div>
            )}
            {m.RegistryComment && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Registry Comment:</span> {m.RegistryComment}
              </div>
            )}

            <div className={styles.commentSection}>
              <div className={styles.commentLabel}>Comment</div>
              <div className={styles.commentText}>{m.Comment || "-"}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.matchesGrid}>
      {matches.map((m) => {
        const rowDecision = decisions[m.COIConflictMatchId];
        return (
          <div key={m.COIConflictMatchId} className={styles.matchCard}>
            <div className={styles.matchTopRow}>
              <div className={styles.matchTitleRow}>
                <div className={styles.matchName}>{affiliateDisplayName(m)}</div>
                <span className={styles.matchScoreBadge}>{m.MatchScore}% match</span>
              </div>
              <div className={styles.decisionRow}>
                <button
                  type="button"
                  className={`${styles.decisionBtn} ${rowDecision?.Decision === COI_DECISIONS.PROCEED ? styles.decisionBtnProceedActive : ""}`}
                  onClick={() => setDecision(m.COIConflictMatchId, COI_DECISIONS.PROCEED)}
                >
                  <Icon.CheckCircle /> Proceed
                </button>
                <button
                  type="button"
                  className={`${styles.decisionBtn} ${rowDecision?.Decision === COI_DECISIONS.STOP ? styles.decisionBtnStopActive : ""}`}
                  onClick={() => setDecision(m.COIConflictMatchId, COI_DECISIONS.STOP)}
                >
                  <Icon.XCircle /> Stop
                </button>
              </div>
            </div>

            <div className={styles.matchMeta}>{m.AffiliateTypeCode === AFFILIATE_TYPE_CODES.ENTITY ? "Entity" : "Individual"}</div>

            {m.RelationshipToSECClient && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Relationship:</span> {m.RelationshipToSECClient}
              </div>
            )}
            {registryClientDisplayName(m) && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Client:</span> {registryClientDisplayName(m)}
              </div>
            )}
            {m.PrimaryPartner && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Partner:</span> {m.PrimaryPartner}
              </div>
            )}
            {affiliateIsSecClientLabel(m) && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Affiliate Is Client:</span> {affiliateIsSecClientLabel(m)}
              </div>
            )}
            {m.RegistryComment && (
              <div className={styles.matchDetailRow}>
                <span className={styles.matchDetailLabel}>Registry Comment:</span> {m.RegistryComment}
              </div>
            )}

            <div className={styles.commentSection}>
              <div className={styles.commentLabel}>Comment</div>
              <textarea
                rows={4}
                className={`form-control ${styles.commentBox}`}
                placeholder="Add a comment for this decision"
                value={rowDecision?.Comment ?? ""}
                onChange={(e) => setComment(m.COIConflictMatchId, e.target.value)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default COIResultsApprovalViewer;
